import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
// Native MUI Components
import { 
  Button,
  Container, 
  Box, 
  Typography, 
  CircularProgress, 
  Paper
} from '@mui/material';
import { ArrowBack as ArrowLeft } from '@mui/icons-material';
import { SignUpData } from '@/types/signup';
import { Trip } from '@/types';
import { getTripById } from '@/services/tripService';
import { signUpService } from '@/services/signUpService';
import TripSignUpForm from '@/components/signup/TripSignUpForm';
import Header from '@/components/layout/Header';
import { useToast } from '@/hooks/use-toast';
import { useI18n } from '@/hooks/useI18n';

const TripSignUpPage = () => {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [trip, setTrip] = useState<Trip | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!tripId) {
      navigate('/');
      return;
    }

    const fetchTrip = async () => {
      try {
        const foundTrip = await getTripById(tripId);
        if (!foundTrip) {
          toast({ title: t('pages.tripSignUp.tripNotFoundToast'), variant: "destructive" });
          navigate('/');
        } else {
          setTrip(foundTrip);
        }
      } catch (error) {
        console.error("Failed to fetch trip:", error);
        toast({ title: t('pages.tripSignUp.errorToast'), description: t('pages.tripSignUp.errorLoadingTrip'), variant: "destructive" });
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    fetchTrip();
  }, [tripId, navigate, toast, t]);

  const handleSignUpSubmit = async (signUpData: SignUpData) => { 
    if (!tripId) {
      console.error("TripID is missing");
      toast({
        title: t('pages.tripSignUp.errorToast'),
        description: t('pages.tripSignUp.tripIdMissing'),
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    setIsSubmitted(false); // Reset submitted state for retry attempts
    try {
      await signUpService.submitSignUp(tripId, signUpData);
      console.log('Sign-up submitted successfully');
      setIsSubmitted(true);
    } catch (error) {
      console.error('Error submitting sign up:', error);
      toast({
        title: t('pages.tripSignUp.signUpFailed'),
        description: t('pages.tripSignUp.signUpFailedDescription'),
        variant: "destructive",
      });
      // isSubmitted bleibt false, so dass das Formular sichtbar bleibt
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '100vh', 
          gap: 2 
        }}>
          <CircularProgress />
          <Typography color="text.secondary">{t('pages.tripSignUp.loadingTripDetails')}</Typography>
        </Box>
      </Container>
    );
  }

  if (!trip) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '100vh', 
          gap: 2 
        }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('pages.tripSignUp.tripNotFound')}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {t('pages.tripSignUp.tripNotFoundDescription')}
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => navigate('/')}
            startIcon={<ArrowLeft />}
          >
            {t('pages.tripSignUp.goHome')}
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      <Header 
        title={t('pages.tripSignUp.title')}
        onBack={() => navigate('/')}
      />
      
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <TripSignUpForm 
          trip={trip} 
          onSubmit={handleSignUpSubmit} 
          loading={isSubmitting}
          submitted={isSubmitted}
          onBackToHome={handleBackToHome}
        />
      </Container>
    </Box>
  );
};

export default TripSignUpPage;
